import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonLoading from '@mui/lab/LoadingButton';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import ActivityIndicator from 'components/ActivityIndicatorComponent';
import { BackToListButton } from 'components/ButtonComponent/ButtonBackToList';
import { MainLayoutComponent } from 'components/LayoutComponent/SidebarLayout/MainLayoutComponent';
import { ModalLeavePageConfirmationComponent } from 'components/ModalComponent/ModalLeavePageConfirmationComponent';
import { useLeavePageConfirm } from 'hooks/usePageLeaveConfirm';
import { DASHBOARD_VIEW_MODE } from 'constant/DashboardTypeConstant';
import { SUBJECT_TYPE } from 'constant/DataInputConstant';
import { RULES_CONSTANT } from 'constant/RuleConstant';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import {
  useAttributeDropdownTableDefinition,
  useUpsertAttributeData,
} from 'services/v1/SystemTenant/AROKMS/AttributeService';
import { useGetAllKTreeDefinitionList } from 'services/v1/SystemTenant/AROKMS/KTreeBuilderService';
import {
  useDataTypesDropdown,
  useRuleTypesDropdown,
  useSaveTableDefinition,
  useTableDefinitionDetails,
} from 'services/v1/SystemTenant/AROKMS/TableDefinitionService';
import {
  useGetSubjectDetails,
  useSubjectDropdownTableDefinition,
} from 'services/v1/SystemTenant/AROKMS/SubjectService';
import { getErrorMessage } from 'utils/Error';
import { AxiosDefaultErrorEntity } from 'types';
import { DataTypeItem, RuleTypeItem } from 'types/api/SystemTenant/AROKMS/TableDefinitionTypes';
import {
  EMPTY_FIELD_VALIDATION,
  RuleParameterOption,
  RuleParameterSchemaItem,
  TableDefinitionEditorMode,
  TableDefinitionEditorValues,
  TableDefinitionKind,
} from '../types';
import { useRuleParameterOptions } from '../hooks/useRuleParameterOptions';
import { countSavedParameters, hydrateParameterValues } from '../utils/parameterHydration';
import {
  createInitialParameterValues,
  expandDynamicParameterSchema,
  parseParameterSchema,
} from '../utils/parameterSchema';
import { areRequiredParametersComplete } from '../utils/parameterSerialization';
import { getRuleParameterValidationError } from '../utils/parameterValidation';
import { buildTableDefinitionEditorPayload } from '../utils/tableDefinitionPayload';
import { buildTableDefinitionV2ListPath } from '../utils/tableDefinitionRoutes';
import {
  isFieldValidationValid,
  parseFieldValidation,
  supportsFieldValidation,
} from '../utils/tableDefinitionValidation';
import { FieldValidationSection } from './FieldValidationSection';
import { KTreeConfigurationSection } from './KTreeConfigurationSection';
import { RuleParameterSection } from './RuleParameterSection';
import { EditorField, EditorSectionCard, editorControlStyles } from './EditorSectionCard';

const ASSOCIATED_SUBJECT_DATA_TYPE: DataTypeItem = {
  id: 6,
  label: 'Associated Subject Rule',
  code: 'TYPE_ASSOCIATED_SUBJECT',
  value: '6',
};

const INITIAL_VALUES: TableDefinitionEditorValues = {
  attribute: null,
  dataType: null,
  associatedSubject: null,
  displayName: '',
  ruleType: null,
  ktreeDefinition: null,
  inferenceKTreeDefinition: null,
  allowEmpty: false,
  parameters: {},
  validation: EMPTY_FIELD_VALIDATION,
};

const SUPPORTED_FIELD_TYPES = new Set([
  'textinput',
  'numberinput',
  'switch-toggle',
  'dropdown',
  'dropdown-dynamic',
  'score-mapping-table',
]);

interface AttributeEditorOption extends RuleParameterOption {
  isNew?: boolean;
}

export function TableDefinitionEditorForm({
  mode,
  subjectId,
  definitionKind,
  tableDefinitionId,
}: {
  mode: TableDefinitionEditorMode;
  subjectId: string;
  definitionKind: TableDefinitionKind;
  tableDefinitionId?: string;
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { dashboardViewMode } = useSelector((state: RootState) => state.auth);
  const [parameterSchema, setParameterSchema] = useState<RuleParameterSchemaItem[]>([]);
  const [schemaError, setSchemaError] = useState('');
  const [allowNavigation, setAllowNavigation] = useState(false);
  const initializedDefinitionRef = useRef<string | null>(null);
  const previousAssociatedSubjectLabel = useRef('');
  const parameterSectionRef = useRef<HTMLDivElement>(null);
  const validationSectionRef = useRef<HTMLDivElement>(null);

  const { data: subjectDetails, isLoading: isLoadingSubject } = useGetSubjectDetails(subjectId);
  const subjectType = subjectDetails?.data.type;
  const { data: dataTypes, isLoading: isLoadingDataTypes } = useDataTypesDropdown({ subjectType });
  const { data: ruleTypes, isLoading: isLoadingRuleTypes } = useRuleTypesDropdown({ subjectType });
  const { data: attributes, isLoading: isLoadingAttributes } = useAttributeDropdownTableDefinition(subjectId);
  const { data: associatedSubjects, isLoading: isLoadingAssociatedSubjects } =
    useSubjectDropdownTableDefinition(subjectId);
  const { data: kTrees, isLoading: isLoadingKTrees } = useGetAllKTreeDefinitionList();
  const {
    data: definitionDetails,
    isLoading: isLoadingDefinition,
    isError: isDefinitionError,
    error: definitionError,
  } = useTableDefinitionDetails({
    tableDefinitionId,
    enabled: mode === 'edit',
  });
  const { mutateAsync: saveDefinition, isLoading: isSaving } = useSaveTableDefinition(definitionKind);
  const { mutateAsync: createAttribute, isLoading: isCreatingAttribute } = useUpsertAttributeData();

  const formik = useFormik<TableDefinitionEditorValues>({
    initialValues: {
      ...INITIAL_VALUES,
      dataType: definitionKind === 'associated-subject' ? ASSOCIATED_SUBJECT_DATA_TYPE : null,
    },
    onSubmit: () => {},
  });

  const isInferenceRule =
    formik.values.ruleType?.code === RULES_CONSTANT.ASSOCIATED_SUBJECT.RULE_KTREE_PARENT_ATTRIBUTE_INFERENCE;

  const parameterOptions = useRuleParameterOptions({
    subjectId,
    ruleType: formik.values.ruleType,
    currentAttributeLabel: formik.values.attribute?.label,
    associatedSubjectLabel: formik.values.associatedSubject?.label,
    associatedSubjectId: formik.values.associatedSubject?.value,
    inferenceKTreeDefinitionId: formik.values.inferenceKTreeDefinition?.value,
    parameters: formik.values.parameters,
  });

  const attributeOptions = useMemo<AttributeEditorOption[]>(
    () =>
      (attributes?.data || []).map((attribute) => ({
        label: attribute.label,
        value: attribute.value,
        persistentName: attribute.label,
      })),
    [attributes?.data]
  );
  const associatedSubjectOptions = useMemo<RuleParameterOption[]>(
    () =>
      (associatedSubjects?.data || []).map((subject) => ({
        label: subject.subject,
        value: subject.id,
        persistentName: subject.subject,
      })),
    [associatedSubjects?.data]
  );
  const kTreeOptions = useMemo<RuleParameterOption[]>(
    () =>
      (kTrees?.data || []).map((kTree) => ({
        ...kTree,
        persistentName: kTree.label,
      })),
    [kTrees?.data]
  );
  const availableRuleTypes = useMemo(() => {
    const selectedDataTypeId = formik.values.dataType?.id || formik.values.dataType?.value;
    if (!selectedDataTypeId) return [];
    return (ruleTypes?.data || []).filter(
      (rule) => String(rule.dataType?.id) === String(selectedDataTypeId)
    );
  }, [formik.values.dataType, ruleTypes?.data]);

  const setRuleType = (ruleType: RuleTypeItem | null) => {
    let nextSchema: RuleParameterSchemaItem[] = [];
    let nextSchemaError = '';
    try {
      nextSchema = parseParameterSchema(ruleType?.paramsValue);
    } catch (error) {
      nextSchemaError = error instanceof Error ? error.message : 'Unable to read the selected rule parameters.';
    }
    setParameterSchema(nextSchema);
    setSchemaError(nextSchemaError);
    formik.setFieldValue('ruleType', ruleType);
    formik.setFieldValue('parameters', createInitialParameterValues(nextSchema));
    formik.setFieldValue('ktreeDefinition', null);
    formik.setFieldValue('inferenceKTreeDefinition', null);
    if (!supportsFieldValidation(ruleType?.code)) {
      formik.setFieldValue('validation', EMPTY_FIELD_VALIDATION);
    }
  };

  useEffect(() => {
    if (mode !== 'edit' || !definitionDetails?.data || !ruleTypes?.data || !kTrees?.data) return;
    if (initializedDefinitionRef.current === definitionDetails.data.id.toString()) return;

    const details = definitionDetails.data;
    const selectedRule = ruleTypes.data.find((rule) => String(rule.id) === String(details.ruleType.id));
    if (!selectedRule) return;

    let schema: RuleParameterSchemaItem[] = [];
    try {
      const baseSchema = parseParameterSchema(selectedRule.paramsValue || details.ruleType.paramsValue);
      schema = expandDynamicParameterSchema(
        baseSchema,
        countSavedParameters(details.ruleTypeParams, selectedRule.code)
      );
      setSchemaError('');
    } catch (error) {
      setSchemaError(error instanceof Error ? error.message : 'Unable to read the saved rule parameters.');
    }

    const attribute = details.attribute
      ? {
          label: details.attribute.displayName || details.attribute.attribute,
          value: details.attribute.id,
          persistentName: details.attribute.attribute,
        }
      : null;
    const associatedSubject = details.associatedSubjectDefinition?.associatedSubject
      ? {
          label:
            details.associatedSubjectDefinition.associatedSubject.displayName ||
            details.associatedSubjectDefinition.associatedSubject.subject,
          value: details.associatedSubjectDefinition.associatedSubject.id,
          persistentName: details.associatedSubjectDefinition.associatedSubject.subject,
        }
      : null;
    const selectedKTree = details.kTreeDefinition?.id
      ? kTreeOptions.find((item) => String(item.value) === String(details.kTreeDefinition?.id)) || null
      : null;
    const inferenceKTreeId =
      details.inferenceKTreeDefinition?.value ||
      details.inferenceKTreeDefinition?.id ||
      details.inferenceKTreeDefinitionId;
    const selectedInferenceKTree = inferenceKTreeId
      ? kTreeOptions.find((item) => String(item.value) === String(inferenceKTreeId)) || null
      : null;

    const hydratedParameters = {
      ...createInitialParameterValues(schema),
      ...hydrateParameterValues(
        schema,
        details.ruleTypeParams,
        details.ruleTypeParamsTableDefinitionId,
        selectedRule.code
      ),
    };
    const nextValues: TableDefinitionEditorValues = {
      attribute,
      dataType: definitionKind === 'associated-subject' ? ASSOCIATED_SUBJECT_DATA_TYPE : selectedRule.dataType,
      associatedSubject,
      displayName: details.associatedSubjectDefinition?.displayName || '',
      ruleType: selectedRule,
      ktreeDefinition: selectedKTree,
      inferenceKTreeDefinition: selectedInferenceKTree,
      allowEmpty: Boolean(details.allowEmpty),
      parameters: hydratedParameters,
      validation: parseFieldValidation(details.validationPattern),
    };
    setParameterSchema(schema);
    formik.setValues(nextValues, false);
    formik.resetForm({ values: nextValues });
    previousAssociatedSubjectLabel.current = associatedSubject?.label || '';
    initializedDefinitionRef.current = details.id.toString();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definitionDetails?.data, definitionKind, kTrees?.data, mode, ruleTypes?.data]);

  useEffect(() => {
    if (isLoadingDefinition || isLoadingRuleTypes) return;
    const section = searchParams.get('section');
    if (section === 'parameters') parameterSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
    if (section === 'validation') validationSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [isLoadingDefinition, isLoadingRuleTypes, searchParams]);

  const hasUnsupportedField = parameterSchema.some((field) => !SUPPORTED_FIELD_TYPES.has(field.type));
  const hasUnsupportedOptionsSource = parameterSchema.some(
    (field) =>
      (field.type === 'dropdown' || field.type === 'dropdown-dynamic') &&
      !parameterOptions.isSupportedSource(field.optionsSource)
  );
  const expectsRuleParameters =
    formik.values.ruleType?.isRequiredParams === true || formik.values.ruleType?.isRequiredParams === 'Y';
  const hasMissingParameterSchema = Boolean(formik.values.ruleType && expectsRuleParameters && !parameterSchema.length);
  const parametersValid = areRequiredParametersComplete(
    parameterSchema,
    formik.values.parameters,
    formik.values.ruleType?.code
  );
  const parameterValidationError = getRuleParameterValidationError(
    parameterSchema,
    formik.values.parameters
  );
  const baseFieldsValid =
    Boolean(formik.values.ruleType) &&
    (definitionKind === 'associated-attribute'
      ? Boolean(formik.values.attribute && formik.values.dataType)
      : Boolean(formik.values.associatedSubject && formik.values.displayName.trim()));
  const validationValid =
    !supportsFieldValidation(formik.values.ruleType?.code) || isFieldValidationValid(formik.values.validation);
  const isBuilderViewOnly = dashboardViewMode === DASHBOARD_VIEW_MODE.VIEW_ONLY;
  const isStaticSubject = Boolean(subjectDetails?.data.static);
  const isInvalidViewKind =
    subjectDetails?.data.type === SUBJECT_TYPE.VIEW && definitionKind === 'associated-subject';
  const isLockedDefinition = Boolean(definitionDetails?.data.ruleType?.locked);
  const isEditorReadOnly =
    isBuilderViewOnly || isStaticSubject || isInvalidViewKind || isLockedDefinition;
  const canSave =
    baseFieldsValid &&
    parametersValid &&
    !parameterValidationError &&
    validationValid &&
    !schemaError &&
    !hasMissingParameterSchema &&
    !hasUnsupportedField &&
    !hasUnsupportedOptionsSource &&
    !isEditorReadOnly;
  const isLoading =
    isLoadingSubject ||
    isLoadingDataTypes ||
    isLoadingRuleTypes ||
    isLoadingAttributes ||
    isLoadingAssociatedSubjects ||
    isLoadingKTrees ||
    (mode === 'edit' && isLoadingDefinition);

  const returnPath = buildTableDefinitionV2ListPath(subjectId, definitionKind);
  const hasUnsavedChanges = formik.dirty && !allowNavigation && !isSaving;
  useLeavePageConfirm(hasUnsavedChanges, 'You have unsaved table definition changes.');

  const requiredParameterCount = parameterSchema.filter((field) => field.required).length;
  const completedRequiredParameterCount = parameterSchema.filter((field) => {
    if (!field.required) return false;
    const value = formik.values.parameters[field.id];
    if (value && typeof value === 'object' && 'value' in value) return Boolean(value.value);
    return value !== '' && value !== null && value !== undefined;
  }).length;
  const definitionTargetLabel =
    definitionKind === 'associated-attribute'
      ? formik.values.attribute?.label
      : formik.values.associatedSubject?.label;
  const definitionTargetComplete = Boolean(definitionTargetLabel);
  const summaryItems = [
    {
      label: definitionKind === 'associated-attribute' ? 'Attribute' : 'Associated subject',
      value: definitionTargetLabel || 'Not selected',
      complete: definitionTargetComplete,
    },
    {
      label: 'Data type',
      value: formik.values.dataType?.label || 'Not selected',
      complete: Boolean(formik.values.dataType),
    },
    {
      label: 'Rule type',
      value: formik.values.ruleType?.label || 'Not selected',
      complete: Boolean(formik.values.ruleType),
    },
    {
      label: 'Required parameters',
      value: formik.values.ruleType
        ? hasMissingParameterSchema
          ? 'Schema unavailable'
          : requiredParameterCount
          ? `${completedRequiredParameterCount} of ${requiredParameterCount} complete`
          : 'None required'
        : 'Waiting for rule',
      complete:
        Boolean(formik.values.ruleType) &&
        !hasMissingParameterSchema &&
        parametersValid &&
        !parameterValidationError,
    },
  ];
  const subjectName = subjectDetails?.data.displayName || subjectDetails?.data.subject || 'Subject';
  const subjectTypeLabel = subjectDetails?.data.type as unknown as string | undefined;
  const definitionKindLabel =
    definitionKind === 'associated-attribute' ? 'Associated attribute' : 'Associated subject';

  const handleCancel = () => navigate(returnPath);

  const handleSubmit = async () => {
    if (!canSave) return;
    try {
      const payload = buildTableDefinitionEditorPayload({
        id: tableDefinitionId,
        subjectId,
        kind: definitionKind,
        values: formik.values,
        parameterSchema,
        attributeOptions,
      });
      await saveDefinition(payload);
      toast.success(`Table definition ${mode === 'create' ? 'created' : 'updated'} successfully`);
      setAllowNavigation(true);
      window.setTimeout(() => navigate(returnPath), 0);
    } catch (error) {
      toast.error(getErrorMessage(error as AxiosDefaultErrorEntity));
    }
  };

  const handleAttributeChange = async (option: AttributeEditorOption | string | null) => {
    if (!option) {
      formik.setFieldValue('attribute', null);
      return;
    }
    if (typeof option === 'string' || option.isNew) {
      const attributeName = typeof option === 'string' ? option : String(option.value);
      try {
        const response = await createAttribute({ attribute: attributeName });
        formik.setFieldValue('attribute', {
          label: response.data.attribute,
          value: response.data.id,
          persistentName: response.data.attribute,
        });
        toast.success('Attribute created successfully');
      } catch (error) {
        toast.error(getErrorMessage(error as AxiosDefaultErrorEntity));
      }
      return;
    }
    formik.setFieldValue('attribute', option);
  };

  return (
    <MainLayoutComponent
      pageTitle={`${mode === 'create' ? 'Create' : 'Edit'} Table Definition`}
      breadcrumbs={['Dashboard', 'Data Builder', 'Table Definition V2']}
    >
      <Box sx={{ bgcolor: '#FFFFFF', p: { xs: 1, md: 1.5 }, minHeight: 'calc(100vh - 120px)' }}>
        <Box maxWidth={1240} mx='auto'>
          <Box
            sx={{
              pb: 1.5,
              borderBottom: '1px solid #E3E8EF',
            }}
          >
            <BackToListButton title='Back to table definitions' onClick={handleCancel} sx={{ mb: 0.75 }} />
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent='space-between'
              alignItems={{ xs: 'flex-start', md: 'flex-end' }}
              spacing={1}
            >
              <Stack spacing={0.35}>
                <Typography component='h1' fontSize={{ xs: 19, md: 21 }} fontWeight={700} color='#3B4797'>
                  {mode === 'create' ? 'Create' : 'Edit'} {definitionKindLabel.toLowerCase()}
                </Typography>
                <Stack direction='row' spacing={0.75} alignItems='center' flexWrap='wrap' useFlexGap>
                  <Typography color='#667085' fontSize={12.5}>
                    Subject: <strong>{subjectName}</strong>
                  </Typography>
                  <Typography color='#C0C7D2' fontSize={12}>
                    •
                  </Typography>
                  <Typography color='#667085' fontSize={12.5}>
                    {definitionKindLabel}
                  </Typography>
                  {subjectTypeLabel && (
                    <>
                      <Typography color='#C0C7D2' fontSize={12}>
                        •
                      </Typography>
                      <Typography color='#667085' fontSize={12.5}>
                        {subjectTypeLabel}
                      </Typography>
                    </>
                  )}
                </Stack>
              </Stack>
              {formik.dirty && (
                <Stack direction='row' spacing={0.75} alignItems='center'>
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#D97706' }} />
                  <Typography color='#9A5B08' fontSize={12} fontWeight={700}>
                    Unsaved changes
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Box>

          {isLoading ? (
            <Paper variant='outlined' sx={{ mt: 2.5, borderRadius: 2.5, borderColor: '#E1E8F2' }}>
              <Stack height={450} justifyContent='center'>
                <ActivityIndicator />
              </Stack>
            </Paper>
          ) : (
            <>
              <Stack gap={1.5} mt={2.5}>
                {isDefinitionError && <Alert severity='error'>{getErrorMessage(definitionError)}</Alert>}
                {isStaticSubject && (
                  <Alert severity='warning'>Static subject definitions are view-only and must be changed in code.</Alert>
                )}
                {isBuilderViewOnly && <Alert severity='warning'>Your current builder mode is read-only.</Alert>}
                {isLockedDefinition && <Alert severity='warning'>This definition is locked by its rule type.</Alert>}
                {isInvalidViewKind && (
                  <Alert severity='error'>View subjects do not support associated-subject definitions.</Alert>
                )}
              </Stack>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 300px' },
                  gap: 2.5,
                  mt: 2.5,
                  alignItems: 'start',
                }}
              >
                <Stack gap={2.5} minWidth={0}>
                  <EditorSectionCard
                    icon={<DescriptionOutlinedIcon fontSize='small' />}
                    title='Definition details'
                    description='Choose the target field and the rule that controls its behavior.'
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                        gap: 2.25,
                      }}
                    >
                      {definitionKind === 'associated-attribute' ? (
                        <>
                          <EditorField
                            label='Attribute'
                            required
                            hint='Select an existing attribute or type a new name to create one.'
                          >
                            <Autocomplete<AttributeEditorOption, false, false, true>
                              freeSolo
                              value={formik.values.attribute}
                              options={attributeOptions}
                              disabled={isEditorReadOnly}
                              loading={isLoadingAttributes || isCreatingAttribute}
                              getOptionLabel={(option) =>
                                typeof option === 'string' ? option : option.label || ''
                              }
                              isOptionEqualToValue={(option, value) =>
                                String(option.value) === String(value.value)
                              }
                              filterOptions={(options, params) => {
                                const filtered = createFilterOptions<AttributeEditorOption>()(options, params);
                                const input = params.inputValue.trim();
                                if (
                                  input &&
                                  !options.some((option) => option.label.toLowerCase() === input.toLowerCase())
                                ) {
                                  filtered.push({ label: `Add "${input}"`, value: input, isNew: true });
                                }
                                return filtered;
                              }}
                              onChange={(_, value) => handleAttributeChange(value)}
                              sx={editorControlStyles}
                              renderInput={(params) => (
                                <TextField {...params} size='small' placeholder='Search or create an attribute' />
                              )}
                            />
                          </EditorField>
                          <EditorField label='Data type' required hint='Changing the data type resets the rule type.'>
                            <Autocomplete
                              value={formik.values.dataType}
                              options={dataTypes?.data || []}
                              disabled={isEditorReadOnly}
                              getOptionLabel={(option) => option.label}
                              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                              onChange={(_, value) => {
                                formik.setFieldValue('dataType', value);
                                setRuleType(null);
                              }}
                              sx={editorControlStyles}
                              renderInput={(params) => (
                                <TextField {...params} size='small' placeholder='Select a data type' />
                              )}
                            />
                          </EditorField>
                        </>
                      ) : (
                        <>
                          <EditorField label='Associated subject' required hint='The subject this relationship points to.'>
                            <Autocomplete
                              value={formik.values.associatedSubject}
                              options={associatedSubjectOptions}
                              disabled={isEditorReadOnly}
                              getOptionLabel={(option) => option.label}
                              isOptionEqualToValue={(option, value) =>
                                String(option.value) === String(value.value)
                              }
                              onChange={(_, value) => {
                                const currentName = formik.values.displayName;
                                if (!currentName || currentName === previousAssociatedSubjectLabel.current) {
                                  formik.setFieldValue('displayName', value?.label || '');
                                }
                                previousAssociatedSubjectLabel.current = value?.label || '';
                                formik.setFieldValue('associatedSubject', value);
                              }}
                              sx={editorControlStyles}
                              renderInput={(params) => (
                                <TextField {...params} size='small' placeholder='Select a subject' />
                              )}
                            />
                          </EditorField>
                          <EditorField label='Display name' required hint='The label users see in forms and tables.'>
                            <TextField
                              size='small'
                              disabled={isEditorReadOnly}
                              value={formik.values.displayName}
                              placeholder='Enter a user-facing name'
                              sx={editorControlStyles}
                              onChange={(event) => formik.setFieldValue('displayName', event.target.value)}
                            />
                          </EditorField>
                        </>
                      )}

                      <Box sx={{ gridColumn: { md: '1 / -1' } }}>
                        <EditorField
                          label='Rule type'
                          required
                          hint={
                            formik.values.dataType
                              ? 'Parameters and validation options update automatically for this rule.'
                              : 'Select a data type before choosing a rule.'
                          }
                        >
                          <Autocomplete
                            value={formik.values.ruleType}
                            options={availableRuleTypes}
                            disabled={!formik.values.dataType || isEditorReadOnly}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                            onChange={(_, value) => setRuleType(value)}
                            sx={editorControlStyles}
                            renderInput={(params) => (
                              <TextField {...params} size='small' placeholder='Select how this definition behaves' />
                            )}
                          />
                        </EditorField>
                      </Box>

                      <Box
                        sx={{
                          gridColumn: { md: '1 / -1' },
                          border: '1px solid #E7ECF4',
                          borderRadius: 2,
                          bgcolor: '#FAFBFD',
                          px: 2,
                          py: 0.75,
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              disabled={isEditorReadOnly}
                              checked={formik.values.allowEmpty}
                              sx={{ color: '#98A2AE', '&.Mui-checked': { color: '#42BB93' } }}
                              onChange={(event) => formik.setFieldValue('allowEmpty', event.target.checked)}
                            />
                          }
                          label={
                            <Stack>
                              <Typography color='#344054' fontSize={13} fontWeight={700}>
                                Allow empty values
                              </Typography>
                              <Typography color='#7B8798' fontSize={12}>
                                Users may leave this field blank when entering data.
                              </Typography>
                            </Stack>
                          }
                        />
                      </Box>
                    </Box>
                  </EditorSectionCard>

                  {formik.values.ruleType && (
                    <KTreeConfigurationSection
                      ruleTypeCode={formik.values.ruleType.code}
                      kTreeOptions={kTreeOptions}
                      isLoading={isLoadingKTrees}
                      disabled={isEditorReadOnly}
                      ktreeDefinition={formik.values.ktreeDefinition}
                      inferenceKTreeDefinition={formik.values.inferenceKTreeDefinition}
                      onKTreeChange={(value) => formik.setFieldValue('ktreeDefinition', value)}
                      onInferenceKTreeChange={(value) => {
                        formik.setFieldValue('inferenceKTreeDefinition', value);
                        formik.setFieldValue('parameters', {
                          ...formik.values.parameters,
                          PARENT_NODE_ATTRIBUTE: '',
                          SOURCE_ATTRIBUTE: '',
                        });
                      }}
                    />
                  )}

                  {isInferenceRule && !formik.values.inferenceKTreeDefinition && (
                    <Alert severity='info'>Select an inference KTree to unlock its hierarchy parameters.</Alert>
                  )}

                  <RuleParameterSection
                    schema={parameterSchema}
                    values={formik.values.parameters}
                    ruleSelected={Boolean(formik.values.ruleType)}
                    parametersExpected={expectsRuleParameters}
                    disabled={isEditorReadOnly}
                    onValuesChange={(values) => formik.setFieldValue('parameters', values)}
                    onSchemaChange={setParameterSchema}
                    getOptions={parameterOptions.getOptions}
                    isLoading={parameterOptions.isLoading}
                    error={
                      schemaError ||
                      (hasUnsupportedOptionsSource
                        ? 'The selected rule uses an unsupported parameter option source.'
                        : '') ||
                      parameterValidationError ||
                      (parameterOptions.inferenceOptionsError
                        ? getErrorMessage(parameterOptions.inferenceOptionsError)
                        : '')
                    }
                    sectionRef={parameterSectionRef}
                  />

                  {definitionKind === 'associated-attribute' &&
                    supportsFieldValidation(formik.values.ruleType?.code) && (
                      <FieldValidationSection
                        values={formik.values.validation}
                        disabled={isEditorReadOnly}
                        onChange={(values) => formik.setFieldValue('validation', values)}
                        sectionRef={validationSectionRef}
                      />
                    )}
                </Stack>

                <Stack component='aside' gap={2} sx={{ position: { lg: 'sticky' }, top: { lg: 88 } }}>
                  <Paper
                    variant='outlined'
                    sx={{ p: 1.75, borderRadius: 2, borderColor: '#E1E8F2' }}
                  >
                    <Stack direction='row' spacing={1} alignItems='center'>
                      <ChecklistRoundedIcon sx={{ color: '#3B4797' }} />
                      <Typography color='#28356D' fontSize={14} fontWeight={800}>
                        Definition summary
                      </Typography>
                    </Stack>
                    <Divider sx={{ my: 1.75, borderColor: '#E8EDF4' }} />
                    <Stack gap={1.75}>
                      {summaryItems.map((item) => (
                        <Stack key={item.label} direction='row' spacing={1.25} alignItems='flex-start'>
                          {item.complete ? (
                            <CheckCircleRoundedIcon sx={{ color: '#42BB93', fontSize: 19, mt: 0.15 }} />
                          ) : (
                            <RadioButtonUncheckedRoundedIcon sx={{ color: '#B6C0CF', fontSize: 19, mt: 0.15 }} />
                          )}
                          <Stack minWidth={0}>
                            <Typography color='#7B8798' fontSize={11.5}>
                              {item.label}
                            </Typography>
                            <Typography color='#344054' fontSize={12.5} fontWeight={700} noWrap>
                              {item.value}
                            </Typography>
                          </Stack>
                        </Stack>
                      ))}
                    </Stack>
                  </Paper>

                  <Paper
                    variant='outlined'
                    sx={{ p: 1.75, borderRadius: 2, borderColor: '#DCE7F7', bgcolor: '#F6FAFF' }}
                  >
                    <Typography color='#344054' fontSize={12.5} fontWeight={700}>
                      Save readiness
                    </Typography>
                    <Typography color='#667085' fontSize={12} mt={0.5} lineHeight={1.5}>
                      {isEditorReadOnly
                        ? 'This definition is currently read-only.'
                        : canSave
                        ? 'All required information is complete.'
                        : 'Complete the required fields marked with an asterisk.'}
                    </Typography>
                  </Paper>
                </Stack>
              </Box>

              <Paper
                variant='outlined'
                sx={{
                  position: 'sticky',
                  bottom: 12,
                  zIndex: 5,
                  mt: 2.5,
                  px: { xs: 1.5, md: 2 },
                  py: 1.25,
                  borderRadius: 2,
                  borderColor: '#D8E1ED',
                  bgcolor: 'rgba(255, 255, 255, 0.96)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent='space-between'
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  spacing={1.5}
                >
                  <Stack>
                    <Typography color={canSave ? '#217A5C' : '#344054'} fontSize={13} fontWeight={800}>
                      {isEditorReadOnly
                        ? 'View-only definition'
                        : canSave
                        ? 'Ready to save'
                        : 'Definition is incomplete'}
                    </Typography>
                    <Typography color='#7B8798' fontSize={11.5}>
                      {formik.dirty ? 'Your latest changes have not been saved.' : 'No unsaved changes.'}
                    </Typography>
                  </Stack>
                  <Stack direction='row' justifyContent='flex-end' gap={1.5}>
                    <Button
                      variant='main-table-panel-border'
                      disabled={isSaving}
                      onClick={handleCancel}
                      sx={{ minWidth: 96, boxShadow: 'none' }}
                    >
                      Cancel
                    </Button>
                    <ButtonLoading
                      variant='main-table-panel'
                      loading={isSaving}
                      disabled={!canSave || isSaving}
                      onClick={handleSubmit}
                      sx={{ minWidth: 150, boxShadow: 'none' }}
                    >
                      {mode === 'create' ? 'Create definition' : 'Save changes'}
                    </ButtonLoading>
                  </Stack>
                </Stack>
              </Paper>
            </>
          )}
        </Box>
      </Box>
      <ModalLeavePageConfirmationComponent when={hasUnsavedChanges} />
    </MainLayoutComponent>
  );
}
